// src/components/PromoStrip.js
// Horizontal promo strip embedded in Explore feed and Detail pages
import React, { useEffect, useState } from 'react';
import { B } from '../constants';
import { promos as promosAPI } from '../api';
import ReelsFeed from './ReelsFeed';

export default function PromoStrip({ destination, user, label = '🎬 Travel Stories', onPlanTrip, onAuthRequired }) {
  const [promos,  setPromos]  = useState([]);
  const [viewer,  setViewer]  = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = destination ? { destination } : {};
    promosAPI.list(params)
      .then(d => setPromos((d.promos || []).slice(0, 12)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [destination]);

  if (!loading && promos.length === 0) return null;

  return (
    <div style={{ marginBottom: 8 }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, padding: '0 2px' }}>
        <div>
          <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 700, color: B.night }}>{label}</h3>
          {destination && <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: B.muted, marginTop: 2 }}>From creators who've been to {destination}</p>}
        </div>
        <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: B.forest, fontWeight: 700, cursor: 'default' }}>
          {promos.length} live {promos.length === 1 ? 'promo' : 'promos'}
        </span>
      </div>

      {/* Horizontal scroll */}
      <div style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 8, scrollbarWidth: 'none' }}>
        <style>{`.promo-strip::-webkit-scrollbar{display:none}`}</style>
        {loading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div key={i} style={{ flexShrink: 0, width: 160, height: 260, borderRadius: 16, background: '#eee', animation: 'pulse 1.5s ease infinite' }} />
            ))
          : promos.map((promo, i) => (
              <PromoStripCard key={promo.id} promo={promo} onClick={() => setViewer({ promos, startIndex: i })} />
            ))
        }
      </div>

      {viewer && (
        <ReelsFeed
          promos={viewer.promos}
          startIndex={viewer.startIndex}
          onClose={() => setViewer(null)}
          onDeleted={(id) => {
            setPromos(prev => prev.filter(p => p.id !== id));
            setViewer(v => (v ? { ...v, promos: v.promos.filter(p => p.id !== id) } : v));
          }}
          user={user}
          onAuthRequired={onAuthRequired && (() => { setViewer(null); onAuthRequired(); })}
          onPlanTrip={onPlanTrip && ((p) => { setViewer(null); onPlanTrip(p); })}
        />
      )}
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}`}</style>
    </div>
  );
}

function PromoStripCard({ promo, onClick }) {
  const isInfluencer = promo.user?.isInfluencer;
  return (
    <div onClick={onClick} style={{ flexShrink: 0, width: 160, borderRadius: 16, overflow: 'hidden', cursor: 'pointer', background: B.night, position: 'relative', transition: 'transform 0.2s', boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}
      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.03)'}
      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>

      {/* Thumbnail */}
      <div style={{ height: 220, position: 'relative', overflow: 'hidden' }}>
        {promo.thumbnailUrl
          ? <img src={promo.thumbnailUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <div style={{ width: '100%', height: '100%', background: `linear-gradient(135deg,${B.forest},${B.night})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40 }}>🎬</div>
        }
        {/* Gradient overlay */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,rgba(0,0,0,0.7) 0%,transparent 50%)' }} />

        {/* Play icon */}
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 38, height: 38, borderRadius: '50%', background: 'rgba(255,255,255,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>▶</div>

        {/* Creator badge */}
        {isInfluencer && (
          <div style={{ position: 'absolute', top: 8, left: 8, background: `linear-gradient(135deg,${B.amber},#ff6b35)`, color: 'white', fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 10, fontFamily: "'Montserrat',sans-serif" }}>✨ CREATOR</div>
        )}

        {/* Time left */}
        <div style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.6)', color: 'white', fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 10, fontFamily: "'Montserrat',sans-serif" }}>{promo.timeLeft}</div>

        {/* Bottom text */}
        <div style={{ position: 'absolute', bottom: 8, left: 8, right: 8 }}>
          <p style={{ fontFamily: "'Playfair Display',serif", color: 'white', fontSize: 13, fontWeight: 700, lineHeight: 1.3 }}>{promo.title}</p>
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ width: 22, height: 22, borderRadius: '50%', background: `linear-gradient(135deg,${B.forest},${B.mint})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: 'white', fontWeight: 800, flexShrink: 0 }}>
          {promo.user?.name?.[0]}
        </div>
        <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.7)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {promo.user?.name}
        </span>
        <span style={{ marginLeft: 'auto', fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>👁 {promo.views}</span>
      </div>
    </div>
  );
}
